export default function FortMyersTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Fort Myers Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Fort Myers travelers actually move helps us anticipate the passport urgencies that arise here. From spring training road trips to family Caribbean cruises, Lee County travel operates on a rhythm shaped by seasons, sports, and the river.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* RSW Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">RSW — Southwest Florida International</h3>
                <p className="text-gray-400 text-xs">Fort Myers — the primary airport for Lee County</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              RSW is Fort Myers' own international airport, serving as the gateway for Lee and Charlotte counties. Seasonal flight schedules ramp up dramatically November through April, with direct service to Toronto, Montreal, and seasonal European connections. Many Fort Myers residents book international trips departing from RSW with tight passport validity requirements — some countries require six months beyond your return date.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking RSW international departures. If it expires within six months of your return, renew it first — or call us for expedited processing.
              </p>
            </div>
          </div>

          {/* Cruise Departures */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Cruise Departures from South Florida</h3>
                <p className="text-gray-400 text-xs">Port Everglades, Port Miami &amp; Port Tampa Bay</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Fort Myers families are prolific cruisers — it's one of the most affordable ways for multi-generational groups to travel together. Residents regularly drive east to Fort Lauderdale, Miami, or north to Tampa for Caribbean, Mexican Riviera, and repositioning sailings. Many book cruises 30–60 days out during promotional sales — then discover their passport is expired or lacks blank pages for visa stamps.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Closed-loop Caribbean cruises only require a birth certificate, but any international itinerary — or a flight connection — requires a valid passport. Verify your cruise line's policy before sailing.
              </p>
            </div>
          </div>

          {/* Spring Training */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-medal-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Spring Training Season</h3>
                <p className="text-gray-400 text-xs">February–March travel spike</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Every spring, thousands of Red Sox and Twins fans converge on Fort Myers for Grapefruit League games at JetBlue Park and Hammond Stadium. Many snowbirds from the Northeast and Midwest fly in for the season — and then plan extended trips to Toronto, Montreal, or European destinations before heading home. Passport discoveries often happen right before these secondary trips, creating urgent renewal needs in February and March.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> If you are a snowbird planning post-season travel back north or abroad, check your passport expiration in January — before spring training crowds create processing backlogs.
              </p>
            </div>
          </div>

          {/* Family & Eco-Tourism */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-tree-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Family Vacations &amp; Eco-Tours</h3>
                <p className="text-gray-400 text-xs">Spring break, summer adventures, Costa Rica</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Fort Myers families are increasingly booking eco-tourism trips to Costa Rica, Belize, and Panama — destinations that combine adventure with the nature-focused values of the Caloosahatchee region. These trips are often booked during winter promotional windows for spring break or summer departures. Multi-child passport renewals are common, as families discover multiple expired documents at once.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Children's passports expire every 5 years. If you are planning a family trip with kids, check all passports — not just your own — at least 3 months before departure.
              </p>
            </div>
          </div>
        </div>

        {/* Sanibel / Eco-tourism callout */}
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-sun-cloudy-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Sanibel, Captiva &amp; Beach Tourism</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Fort Myers Beach and the Sanibel-Captiva islands draw visitors from across the country — many of whom then extend their Florida stay with international travel. Whether it is a Canadian snowbird heading home, a Midwest family continuing to a Caribbean cruise, or a retired couple flying to Portugal after shelling season, the transition from domestic beach tourism to international travel creates last-minute passport needs.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}